import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useInvitationValidate, buildInvitationLog, CustomerLifecycleStatus } from '@bingads-webui-campaign-react/accept-invitation-api';
import type { InvitationLogFields, InvitationValidationReason } from '@bingads-webui-campaign-react/accept-invitation-api';
import {
  Button,
  Image,
  mergeClasses,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  ClockRegular,
  QuestionCircleRegular,
} from '@fluentui/react-icons';

import { useUserInvitationSelectionPageStyles } from './use-user-invitation-selection-page-styles';
import { InvitationChoiceView } from './invitation-choice-view';
import { InvitationAadOnlyView } from './invitation-aad-only-view';
import { InvitationIdpSelectionView, type InvitationIdpView } from './invitation-idp-selection-view';
import { InvitationErrorView, normalizeValidationReason } from './invitation-error-view';
import { getInvitationSummaryText } from './invitation-summary';
import { HelpResourcesDrawer } from './help-resources-drawer';
import { getInvitationApiBaseUrl } from '../../../utils/url-utils';

import MicrosoftAdvertisingLogo from '../../../../media/microsoft-advertising-logo-idp-header.png';
import InvitationImage from '../../../../media/invitation-image.png';

// The pre-auth invitation experience is a single page with three views:
//   - 'choice': pick between creating a new account or using an existing one
//   - 'create': IDP selection with "Continue with X" copy
//   - 'signin': IDP selection with "Sign in with X" copy
type InvitationView = 'choice' | InvitationIdpView;

export type UserInvitationSelectionPageProps = {
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
};

const getInvitationCodeFromCallback = (): string => {
  const encodedCallback = new URLSearchParams(window.location.search).get('cb');
  if (!encodedCallback) {
    return '';
  }

  try {
    const callbackUrl = new URL(decodeURIComponent(encodedCallback));
    return callbackUrl.searchParams.get('code') || callbackUrl.searchParams.get('Code') || '';
  } catch {
    return '';
  }
};

// Lets the post-auth "Use a different email" flow deep-link the user straight back to the
// "Create an account" IDP view after sign-out (the server redirects /ActivateMsaUser to
// …/userinvitation?…&view=create — server change S-2). Any other/absent value opens the
// default choice view.
const getInitialInvitationView = (): InvitationView => {
  const view = new URLSearchParams(window.location.search).get('view');
  return view === 'create' || view === 'signin' ? view : 'choice';
};

// UI debug hook: `?acceptinvitationdebug=expired|invalid|userslimitreached|inactive` forces the
// matching pre-auth validation error page so designers/devs can preview it without reproducing the
// failing Validate API. (The post-auth ActivationOptions error pages are debugged on the
// accept-invitation page via the same param.)
const getDebugValidationReason = (): InvitationValidationReason | null => {
  const value = new URLSearchParams(window.location.search).get('acceptinvitationdebug')?.toLowerCase();
  switch (value) {
    case 'expired':
      return 'Expired';
    case 'invalid':
      return 'Invalid';
    case 'userslimitreached':
      return 'UsersLimitReached';
    case 'inactive':
      return 'Inactive';
    default:
      return null;
  }
};

export const UserInvitationSelectionPage: React.FC<UserInvitationSelectionPageProps> = ({ logger }) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useUserInvitationSelectionPageStyles();
  const invitationCode = useMemo(() => getInvitationCodeFromCallback(), []);

  // The landing page runs on the campaign-UI host (ui.<env>); the invitation APIs live on the
  // CCUI host. Derive the CCUI origin from the callback URL so Validate doesn't 302 to pagenotfound.
  const invitationApiBaseUrl = useMemo(() => getInvitationApiBaseUrl(), []);
  // UI debug override (?acceptinvitationdebug=expired|invalid|userslimitreached|inactive): drive the
  // same render path as a real validation failure rather than a parallel branch.
  const debugValidationReason = useMemo(() => getDebugValidationReason(), []);
  // When debugging, bypass the Validate call entirely (even if a real invitation code is present),
  // so previewing a mocked error page doesn't issue the backend request it is meant to bypass.
  const invitationCodeForValidation = debugValidationReason ? '' : invitationCode;

  // Emits the shared `InvitationIssues::Result step=PreAuth outcome=… errorCode=… code=…` schema
  // (mirroring the server-side raw logs and the post-auth page) through the landing-page
  // instrumentation logger, which stamps the telemetry session id (SesID). Skipped in debug-preview
  // mode and when the callback URL is missing/malformed (empty code) — a line with no `code` can't be
  // keyed by invitation code (matching the post-auth logger, which also skips empty codes).
  const logInvitation = useCallback((
    outcome: string,
    options: { errorCode?: string; extra?: InvitationLogFields['extra'] } = {}
  ) => {
    if (debugValidationReason || !invitationCode) {
      return;
    }
    logger?.trace(buildInvitationLog({
      step: 'PreAuth',
      outcome,
      errorCode: options.errorCode,
      code: invitationCode,
      extra: options.extra,
    }));
  }, [debugValidationReason, invitationCode, logger]);

  // Pre-auth entry point (report denominator): the invitation IDP-selection landing page was shown.
  // Logged once on mount; repeats within a session dedupe on SesID at report time.
  useEffect(() => {
    logInvitation('LandingLoaded');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: invitationValidation, isLoading: isValidationLoading, error: validationError } = useInvitationValidate(invitationCodeForValidation, invitationApiBaseUrl);
  const daysLeftToJoin = invitationValidation?.daysLeftToJoin;
  // A terminal validation failure (expired / invalid / user-limit) — surfaced as a
  // dedicated error page instead of the IDP-selection views. A recognized
  // `InvitationValidationFailed` fault carries its specific reason; ANY OTHER 400 (an
  // unrecognized error shape from Validate — e.g. a different `error` code or an unparseable
  // body) is still a terminal bad-invitation response and falls back to the same generic
  // "Invalid" error page rather than silently showing the normal invitation page. Only
  // transient/non-400 errors (network / 5xx / no status) fall through so the user can still
  // attempt sign-in.
  const validationFailureReason = useMemo<InvitationValidationReason | null>(() => {
    if (debugValidationReason) {
      return debugValidationReason;
    }
    // A successful Validate can still be terminal when the invited customer is Inactive
    // (or Deleted) — block activation and show the matching error page instead of the
    // IDP-selection views. `null`/absent status (older invitations) keeps the normal flow.
    const lifecycleStatus = invitationValidation?.customerLifecycleStatus;
    if (lifecycleStatus === CustomerLifecycleStatus.Inactive) {
      return 'Inactive';
    }
    if (lifecycleStatus === CustomerLifecycleStatus.Deleted) {
      return 'Invalid';
    }
    if (!validationError) {
      return null;
    }
    if (validationError.error === 'InvitationValidationFailed') {
      return normalizeValidationReason(validationError.reason);
    }
    if (validationError.status === 400) {
      return 'Invalid';
    }
    return null;
  }, [validationError, debugValidationReason, invitationValidation]);
  // A terminal pre-auth failure is a real error outcome, not abandonment. Without this line an
  // expired/invalid invitation is indistinguishable in the funnel from a user who just closed the
  // landing page, so these sessions fall outside the error report entirely.
  const validateFailedLoggedRef = useRef(false);
  useEffect(() => {
    if (!validationFailureReason || validateFailedLoggedRef.current) {
      return;
    }
    validateFailedLoggedRef.current = true;
    logInvitation('ValidateFailed', { errorCode: validationFailureReason });
  }, [validationFailureReason, logInvitation]);

  // The user handed off to an external IDP — separates "left to sign in" (expected to return on the
  // post-auth page) from genuine abandonment on the landing page. Best-effort: the emit races the
  // top-level navigation that follows, so the report must treat it as a lower bound.
  const handleIdpSelected = useCallback((provider: string) => {
    logInvitation('IdpSelected', { extra: { idP: provider } });
  }, [logInvitation]);

  // AAD-enforced invitation: when the inviting customer disallows MSA we skip the
  // create/sign-in choice page and show only the "Sign in with Microsoft" (AAD) option.
  const isAadOnly = invitationValidation?.invitationFlags?.msaSupported === false;

  const [view, setView] = useState<InvitationView>(getInitialInvitationView);

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const openHelp = useCallback(() => {
    logger?.trace('UserInvitationSelectionPage: Opening help resources drawer');
    setIsHelpOpen(true);
  }, [logger]);
  const closeHelp = useCallback(() => {
    setIsHelpOpen(false);
  }, []);

  const goToChoice = useCallback(() => {
    logger?.trace('UserInvitationSelectionPage: Navigating to choice view');
    setView('choice');
  }, [logger]);

  const goToCreate = useCallback(() => {
    logger?.trace('UserInvitationSelectionPage: Navigating to create-account view');
    setView('create');
  }, [logger]);

  const goToSignIn = useCallback(() => {
    logger?.trace('UserInvitationSelectionPage: Navigating to use-existing-account view');
    setView('signin');
  }, [logger]);

  // The personalized "<sender> invited you to be a <role> for <accounts|customer>" line shown under
  // the heading on the choice and AAD-only views. Built from the Validate sender/role/account fields;
  // null (line omitted) when those fields aren't available.
  const invitationSummary = useMemo(
    () => getInvitationSummaryText(i18n, invitationValidation),
    [i18n, invitationValidation]
  );

  const daysLeftPill = useMemo(() => {
    if (daysLeftToJoin === undefined || daysLeftToJoin === null) {
      return null;
    }
    return (
      <Text className={mergeClasses('invitation-days-left-pill', classes.invitationPill)} weight="semibold">
        <ClockRegular className={classes.invitationPillIcon} aria-hidden />
        {i18n.getString(_TL_('{{days}} days left to join'), { days: daysLeftToJoin })}
      </Text>
    );
  }, [classes.invitationPill, classes.invitationPillIcon, daysLeftToJoin, i18n]);

  const helpFooter = useMemo(() => (
    <section className={mergeClasses(classes.section, classes.sectionAdditionalInfo)}>
      <span className={classes.helpFooterContent}>
        <QuestionCircleRegular className={classes.helpFooterIcon} aria-hidden />
        <Text>{i18n.getString(_TL_('Not sure?'))}</Text>
        {' '}
        <Button
          className={mergeClasses('invitation-help-link', classes.helpFooterButton)}
          size="small"
          aria-haspopup="dialog"
          aria-expanded={isHelpOpen}
          onClick={openHelp}
        >
          {i18n.getString(_TL_('Get help here.'))}
        </Button>
      </span>
    </section>
  ), [classes, i18n, isHelpOpen, openHelp]);

  // The page shown depends on the Validate result (invitationFlags.msaSupported), so
  // wait for validation to resolve before deciding between the AAD-only and choice views. The debug
  // override bypasses the wait (no real call to resolve).
  const isValidating = !debugValidationReason && Boolean(invitationCode) && isValidationLoading;

  const renderLeftContent = () => {
    if (isValidating) {
      return (
        <div className={classes.textAndButtons}>
          <Spinner aria-label={i18n.getString(_TL_('Loading'))} />
        </div>
      );
    }

    if (validationFailureReason) {
      return <InvitationErrorView reason={validationFailureReason} />;
    }

    if (isAadOnly) {
      return (
        <InvitationAadOnlyView
          invitationSummary={invitationSummary}
          daysLeftPill={daysLeftPill}
          logger={logger}
          onIdpSelected={handleIdpSelected}
        />
      );
    }

    if (view === 'choice') {
      return (
        <InvitationChoiceView
          invitationSummary={invitationSummary}
          daysLeftPill={daysLeftPill}
          onCreate={goToCreate}
          onSignIn={goToSignIn}
        />
      );
    }

    return (
      <InvitationIdpSelectionView
        variant={view}
        daysLeftPill={daysLeftPill}
        onBack={goToChoice}
        onCreate={goToCreate}
        onSignIn={goToSignIn}
        logger={logger}
        onIdpSelected={handleIdpSelected}
      />
    );
  };

  return (
    <div className={mergeClasses('idp-centered-selection-page', classes.idpSelectionPage)}>
      <div className={classes.content} role="main">
        <section className={mergeClasses('identity-provider-selection-section', classes.section, classes.identitySection)}>
          <div className={classes.contentLeft}>
            <Image
              src={MicrosoftAdvertisingLogo}
              alt={i18n.getString(_TL_('Microsoft Advertising'))}
              width={157}
              height={24}
            />

            {renderLeftContent()}
          </div>

          <div className={mergeClasses(classes.contentRight, 'ad-preview-section')}>
            <Image
              src={InvitationImage}
              width={550}
              height={600}
              alt=""
            />
          </div>
        </section>

        {helpFooter}
      </div>

      <HelpResourcesDrawer
        open={isHelpOpen}
        onClose={closeHelp}
        logger={logger}
      />
    </div>
  );
};
